import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  formatDate,
  formatApiErrorDetail,
} from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  Printer,
  Barcode,
  MagnifyingGlass,
  Trash,
} from "@phosphor-icons/react";
import { printDDT } from "@/lib/ddt";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";

const emptyForm = {
  product_id: "",
  quantity: "",
  note: "",
  reference: "",
};

export default function MovementsPage() {
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [sites, setSites] = useState([]);
  const [type, setType] = useState("in");
  const [form, setForm] = useState(emptyForm);

  const [filter, setFilter] = useState({
    type: "all",
    product_id: "all",
    date_from: "",
    date_to: "",
  });

  const [scanOpen, setScanOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [productResultsOpen, setProductResultsOpen] =
    useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    const params = {};

    if (filter.type !== "all") {
      params.type = filter.type;
    }

    if (filter.product_id !== "all") {
      params.product_id = filter.product_id;
    }

    if (filter.date_from) {
      params.date_from = filter.date_from;
    }

    if (filter.date_to) {
      params.date_to = filter.date_to;
    }

    try {
      const [productsResponse, movementsResponse] =
        await Promise.all([
          api.get("/products"),
          api.get("/movements", { params }),
        ]);

      setProducts(productsResponse.data);
      setMovements(movementsResponse.data);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Errore durante il caricamento dei dati"
      );
    }
  };

  /*
   * Recupera tutti i cantieri già usati negli scarichi.
   * Il campo rimane libero: mentre si scrive, il browser
   * propone i nomi già registrati.
   */
  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get("/movements", {
        params: {
          type: "out",
        },
      });

      const rows = Array.isArray(data) ? data : [];

      const uniqueSites = [];

      rows.forEach((movement) => {
        const name = String(
          movement.reference || ""
        ).trim();

        if (!name) return;

        const alreadyExists = uniqueSites.some(
          (existingName) =>
            existingName.localeCompare(name, "it", {
              sensitivity: "base",
            }) === 0
        );

        if (!alreadyExists) {
          uniqueSites.push(name);
        }
      });

      uniqueSites.sort((a, b) =>
        a.localeCompare(b, "it", {
          sensitivity: "base",
        })
      );

      setSites(uniqueSites);
    } catch {
      setSites([]);
    }
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const onScan = useCallback(async (code) => {
    setScanOpen(false);

    try {
      const { data } = await api.get(
        `/products/by-barcode/${encodeURIComponent(code)}`
      );

      setForm((current) => ({
        ...current,
        product_id: data.id,
        quantity: current.quantity || "1",
      }));

      setProductSearch(`${data.sku} · ${data.name}`);
      setProductResultsOpen(false);

      toast.success(`Prodotto rilevato: ${data.name}`);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Codice non trovato"
      );
    }
  }, []);

  const submit = async (event) => {
    event.preventDefault();

    if (!form.product_id || !form.quantity) {
      toast.error("Prodotto e quantità obbligatori");
      return;
    }

    if (Number(form.quantity) <= 0) {
      toast.error("Inserisci una quantità valida");
      return;
    }

    if (type === "out" && !form.reference.trim()) {
      toast.error("Inserisci il cantiere di riferimento");
      return;
    }

    try {
      await api.post("/movements", {
        product_id: form.product_id,
        type,
        quantity: Number(form.quantity),
        note: form.note,
        reference: form.reference.trim(),
      });

      toast.success(
        type === "in"
          ? "Carico registrato"
          : "Scarico registrato"
      );

      setForm(emptyForm);
      setProductSearch("");
      setProductResultsOpen(false);

      await Promise.all([load(), loadSites()]);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail)
      );
    }
  };

  const removeMovement = async (movement) => {
    const product = products.find(
      (item) => item.id === movement.product_id
    );

    const label =
      movement.type === "in" ? "carico" : "scarico";

    const stockMessage =
      movement.type === "in"
        ? `La giacenza verrà diminuita di ${movement.quantity}.`
        : `La giacenza verrà aumentata di ${movement.quantity}.`;

    const confirmed = window.confirm(
      `Eliminare questo ${label} di ${movement.quantity} ` +
        `${product?.unit || ""} per "${product?.name || "il prodotto"}"?\n\n` +
        `${stockMessage}\n\nL'operazione non può essere annullata.`
    );

    if (!confirmed) return;

    setDeletingId(movement.id);

    try {
      await api.delete(`/movements/${movement.id}`);
      toast.success("Movimento eliminato e giacenza aggiornata");
      await Promise.all([load(), loadSites()]);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Errore durante l'eliminazione del movimento"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const productName = (id) => {
    const product = products.find(
      (item) => item.id === id
    );

    return product
      ? `${product.sku} · ${product.name}`
      : "—";
  };

  const filteredProducts = useMemo(() => {
    const search = productSearch
      .trim()
      .toLowerCase();

    return products
      .filter((product) => {
        if (!search) return true;

        return [
          product.sku,
          product.name,
          product.description,
          product.barcode,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(search)
        );
      })
      .slice(0, 15);
  }, [products, productSearch]);

  const selectedProduct = products.find(
    (product) => product.id === form.product_id
  );

  const selectProduct = (product) => {
    setForm((current) => ({
      ...current,
      product_id: product.id,
    }));

    setProductSearch(
      `${product.sku} · ${product.name}`
    );

    setProductResultsOpen(false);
  };

  const changeMovementType = (newType) => {
    setType(newType);

    setForm((current) => ({
      ...current,
      reference: "",
    }));
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Card
        className="h-fit rounded-2xl border-slate-200 p-5 shadow-sm xl:col-span-1"
        data-testid="movement-form"
      >
        <div className="mb-1 text-lg font-bold tracking-tight text-slate-950">
          Registra movimento
        </div>

        <div className="mb-4 text-xs text-slate-500">
          Carico merce in entrata o scarico verso un
          cantiere
        </div>

        <Tabs
          value={type}
          onValueChange={changeMovementType}
          className="mb-4"
        >
          <TabsList className="grid h-10 grid-cols-2 bg-slate-100">
            <TabsTrigger
              value="in"
              data-testid="tab-carico"
              className="gap-1.5 data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <ArrowDown size={14} weight="bold" />
              Carico
            </TabsTrigger>

            <TabsTrigger
              value="out"
              data-testid="tab-scarico"
              className="gap-1.5 data-[state=active]:bg-orange-600 data-[state=active]:text-white"
            >
              <ArrowUp size={14} weight="bold" />
              Scarico
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <form
          onSubmit={submit}
          className="space-y-4"
        >
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Prodotto *
              </Label>

              <button
                type="button"
                onClick={() => setScanOpen(true)}
                data-testid="scan-barcode-btn"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900"
              >
                <Barcode size={14} weight="bold" />
                Scansiona
              </button>
            </div>

            <div className="relative mt-1">
              <MagnifyingGlass
                size={17}
                weight="bold"
                className="pointer-events-none absolute left-3 top-[22px] z-10 -translate-y-1/2 text-slate-400"
              />

              <Input
                value={productSearch}
                onChange={(event) => {
                  setProductSearch(
                    event.target.value
                  );

                  setForm((current) => ({
                    ...current,
                    product_id: "",
                  }));

                  setProductResultsOpen(true);
                }}
                onFocus={() =>
                  setProductResultsOpen(true)
                }
                placeholder="Scrivi nome, descrizione, SKU o barcode..."
                className="h-11 bg-white pl-10 pr-10"
                autoComplete="off"
                data-testid="movement-product-search"
              />

              {productSearch && (
                <button
                  type="button"
                  onClick={() => {
                    setProductSearch("");

                    setForm((current) => ({
                      ...current,
                      product_id: "",
                    }));

                    setProductResultsOpen(true);
                  }}
                  className="absolute right-3 top-[22px] -translate-y-1/2 text-lg text-slate-400 hover:text-slate-700"
                  aria-label="Cancella prodotto"
                >
                  ×
                </button>
              )}

              {productResultsOpen && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                  {filteredProducts.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-500">
                      Nessun prodotto trovato.
                    </div>
                  ) : (
                    filteredProducts.map(
                      (product) => {
                        const lowStock =
                          product.quantity <=
                          product.min_stock;

                        return (
                          <button
                            key={product.id}
                            type="button"
                            onMouseDown={(event) =>
                              event.preventDefault()
                            }
                            onClick={() =>
                              selectProduct(product)
                            }
                            className="flex w-full items-start justify-between gap-4 rounded-lg px-3 py-3 text-left hover:bg-slate-50"
                          >
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-900">
                                {product.name}
                              </div>

                              <div className="mt-1 text-xs text-slate-500">
                                SKU: {product.sku}
                                {product.barcode
                                  ? ` · Barcode: ${product.barcode}`
                                  : ""}
                              </div>

                              {product.description && (
                                <div className="mt-1 line-clamp-2 text-xs text-slate-500">
                                  {product.description}
                                </div>
                              )}
                            </div>

                            <div className="shrink-0 text-right">
                              <div
                                className={`text-sm font-semibold ${
                                  lowStock
                                    ? "text-red-600"
                                    : "text-emerald-600"
                                }`}
                              >
                                {product.quantity}{" "}
                                {product.unit}
                              </div>

                              <div className="mt-1 text-xs text-slate-400">
                                Disponibile
                              </div>
                            </div>
                          </button>
                        );
                      }
                    )
                  )}
                </div>
              )}
            </div>

            {selectedProduct && (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs">
                <span className="font-medium text-emerald-800">
                  Selezionato:{" "}
                  {selectedProduct.name}
                </span>

                <span className="shrink-0 text-emerald-700">
                  {selectedProduct.quantity}{" "}
                  {selectedProduct.unit} disponibili
                </span>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">
              Quantità *
            </Label>

            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={form.quantity}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  quantity: event.target.value,
                }))
              }
              data-testid="movement-quantity-input"
              className="mt-1"
            />
          </div>

          {type === "in" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Riferimento (DDT / N° ordine)
              </Label>

              <Input
                value={form.reference}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    reference: event.target.value,
                  }))
                }
                placeholder="Inserisci DDT o numero ordine"
                data-testid="movement-reference-input"
                className="mt-1"
              />
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Cantiere di riferimento *
              </Label>

              <Input
                list="cantieri-memorizzati"
                value={form.reference}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    reference: event.target.value,
                  }))
                }
                placeholder="Scrivi o seleziona un cantiere già usato"
                data-testid="movement-reference-input"
                className="mt-1"
                autoComplete="off"
              />

              <datalist id="cantieri-memorizzati">
                {sites.map((siteName) => (
                  <option
                    key={siteName}
                    value={siteName}
                  />
                ))}
              </datalist>

              <div className="mt-1 text-[11px] text-slate-500">
                Durante la digitazione vengono proposti i
                cantieri già utilizzati.
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">
              Note
            </Label>

            <Textarea
              rows={3}
              value={form.note}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  note: event.target.value,
                }))
              }
              placeholder="Inserisci eventuali annotazioni..."
              data-testid="movement-note-input"
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            data-testid="movement-submit-btn"
            className={`h-11 w-full text-white ${
              type === "in"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-orange-600 hover:bg-orange-700"
            }`}
          >
            Registra{" "}
            {type === "in" ? "Carico" : "Scarico"}
          </Button>
        </form>

        <BarcodeScannerDialog
          open={scanOpen}
          onOpenChange={setScanOpen}
          onDetected={onScan}
        />
      </Card>

      <Card
        className="overflow-hidden rounded-2xl border-slate-200 shadow-sm xl:col-span-2"
        data-testid="movements-history"
      >
        <div className="flex flex-col gap-3 border-b border-slate-200 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-lg font-bold tracking-tight">
              Storico movimenti
            </div>

            <div className="text-xs text-slate-500">
              Filtra per tipo, prodotto e periodo
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 md:flex">
            <Select
              value={filter.type}
              onValueChange={(value) =>
                setFilter((current) => ({
                  ...current,
                  type: value,
                }))
              }
            >
              <SelectTrigger
                className="h-9 w-[130px] bg-white"
                data-testid="filter-type"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent className="bg-white">
                <SelectItem value="all">
                  Tutti i tipi
                </SelectItem>
                <SelectItem value="in">
                  Solo Carico
                </SelectItem>
                <SelectItem value="out">
                  Solo Scarico
                </SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.product_id}
              onValueChange={(value) =>
                setFilter((current) => ({
                  ...current,
                  product_id: value,
                }))
              }
            >
              <SelectTrigger
                className="h-9 w-[180px] bg-white"
                data-testid="filter-product"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent className="max-h-72 bg-white">
                <SelectItem value="all">
                  Tutti i prodotti
                </SelectItem>

                {products.map((product) => (
                  <SelectItem
                    key={product.id}
                    value={product.id}
                  >
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filter.date_from}
              onChange={(event) =>
                setFilter((current) => ({
                  ...current,
                  date_from: event.target.value,
                }))
              }
              className="h-9 w-[150px]"
              data-testid="filter-date-from"
            />

            <Input
              type="date"
              value={filter.date_to}
              onChange={(event) =>
                setFilter((current) => ({
                  ...current,
                  date_to: event.target.value,
                }))
              }
              className="h-9 w-[150px]"
              data-testid="filter-date-to"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3 font-medium">
                  Data
                </th>
                <th className="px-5 py-3 font-medium">
                  Tipo
                </th>
                <th className="px-5 py-3 font-medium">
                  Prodotto
                </th>
                <th className="px-5 py-3 text-right font-medium">
                  Qtà
                </th>
                <th className="px-5 py-3 font-medium">
                  DDT / Cantiere
                </th>
                <th className="px-5 py-3 font-medium">
                  Operatore
                </th>
                <th className="px-5 py-3 text-right font-medium">
                  DDT
                </th>
                <th className="px-5 py-3 text-right font-medium">
                  Azioni
                </th>
              </tr>
            </thead>

            <tbody>
              {movements.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-slate-500"
                  >
                    Nessun movimento nel periodo
                    selezionato.
                  </td>
                </tr>
              )}

              {movements.map((movement) => (
                <tr
                  key={movement.id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`movement-row-${movement.id}`}
                >
                  <td className="px-5 py-2.5 text-slate-600 tabular">
                    {formatDate(
                      movement.created_at
                    )}
                  </td>

                  <td className="px-5 py-2.5">
                    <span
                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${
                        movement.type === "in"
                          ? "bg-green-50 text-green-700"
                          : "bg-orange-50 text-orange-700"
                      }`}
                    >
                      {movement.type === "in" ? (
                        <ArrowDown
                          size={12}
                          weight="bold"
                        />
                      ) : (
                        <ArrowUp
                          size={12}
                          weight="bold"
                        />
                      )}

                      {movement.type === "in"
                        ? "Carico"
                        : "Scarico"}
                    </span>
                  </td>

                  <td className="px-5 py-2.5">
                    {productName(
                      movement.product_id
                    )}
                  </td>

                  <td className="px-5 py-2.5 text-right font-medium tabular">
                    {movement.quantity}
                  </td>
                  <td className="px-5 py-2.5 text-slate-600">
                    {movement.reference || "—"}
                  </td>

                  <td className="max-w-[180px] truncate px-5 py-2.5 text-slate-600">
                    {movement.user_name || "—"}
                  </td>

                  <td className="px-5 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => printDDT(movement.id)}
                      data-testid={`ddt-print-${movement.id}`}
                      className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-medium transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                      title={
                        movement.type === "in"
                          ? "Stampa DDT di carico"
                          : "Stampa DDT di scarico"
                      }
                    >
                      <Printer size={14} weight="bold" />
                      DDT
                    </button>
                  </td>

<td className="px-5 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => removeMovement(movement)}
                      disabled={deletingId === movement.id}
                      data-testid={`delete-movement-${movement.id}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Elimina movimento"
                      aria-label="Elimina movimento"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
